import React, { useMemo } from 'react';
import { FormControl, Grid, MenuItem, Paper } from '@mui/material';
import { useDispatch } from 'react-redux';
import { IBuilding, IFloor } from 'spw-redux/modules/projects/types';
import {
  updateScenario,
  setScenarioValue,
} from 'spw-redux/modules/calculators/actions';
import { isNil } from 'ramda';
import { TOTAL_AVAILABLE_USF_TITLE } from 'spw-constants';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { ScenarioHeader } from 'spw-components/downloads/image-header';
import DeskSharingGuide from './desk-sharing-guide';
import HeadcountUtilizationGuide from './headcount-utilization-guide';
import Headcount, { TARGET_USF_LABEL } from './headcount';
import {
  Container,
  FactorContainer,
  FactorSection,
  CustomSelect,
  Content,
  FormTitle,
  ControlsContainer,
  TargetUsfContainer,
  Divider,
  HeadcountContainer,
} from './styles';
import { PlainProgramScenario } from '../../models/Scenario';
import VacancyFactorGuide from './vacancy-factor-guide';

interface IProps {
  building: IBuilding;
  floors?: boolean;
  scenario?: PlainProgramScenario;
  isEditingSavedScenario?: boolean;
  showDecimals: boolean;
  calculatorActions?: JSX.Element;
  isRenderingForExport?: boolean;
}

const UsfScenario: React.FC<IProps> = ({
  building,
  floors = false,
  scenario,
  isEditingSavedScenario = false,
  showDecimals,
  calculatorActions,
  isRenderingForExport,
}) => {
  const { getUnit, convertToUnit, convertToOriginalUnit } = useUnitConversion();
  const dispatch = window.SPWExport ? Function.prototype : useDispatch();

  const selectedFloor = useMemo(() => {
    if (scenario?.floorId) return scenario.floorId;

    const firstFloorWithUsf = building?.floors?.find((f) => f.totalFloorUsf);

    if (firstFloorWithUsf) return firstFloorWithUsf.id;

    return 0;
  }, [scenario, building]);

  const totalAvailableUsfTitle = useMemo(() => {
    if (floors) {
      return TOTAL_AVAILABLE_USF_TITLE.floor;
    }
    return TOTAL_AVAILABLE_USF_TITLE.building;
  }, [floors]);

  if (!scenario) return null;

  const disableTargetUsfInput = window.SPWExport
    ? false
    : scenario?.id && !isEditingSavedScenario;

  const disableHeadcountInputs = disableTargetUsfInput || !scenario?.targetUSF;

  return (
    <Content>
      <Paper elevation={0}>
        {(window.SPWExport || isRenderingForExport) && (
          <ScenarioHeader
            scenario={scenario}
            building={building}
            styles={{ marginBottom: 15 }}
          />
        )}
        <Container>
          {floors && (
            <ControlsContainer>
              <FormControl variant="outlined">
                <CustomSelect
                  label="Floors"
                  value={selectedFloor}
                  disabled={window.SPWExport ? false : !isNil(scenario?.id)}
                  onChange={(e) => {
                    const floorId = e.target.value as number;
                    dispatch(
                      updateScenario(scenario, {
                        floorId,
                        targetUsf: 0,
                        deskSharingRatio: '1.00',
                        headcountUtilization: '100',
                        vacancyFactor: '0.0',
                      })
                    );
                  }}
                >
                  {building?.floors?.map((floor: IFloor) => (
                    <MenuItem
                      key={`${floor.name}-${floor.id}`}
                      value={floor.id}
                      disabled={!floor?.totalFloorUsf}
                    >
                      {floor.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </FormControl>
              {calculatorActions}
            </ControlsContainer>
          )}

          <FormTitle
            style={{ minHeight: '44.5px' }}
            data-testid="seatcount_analysis_title"
          >
            ENTER TARGET {getUnit('area')}
            {!floors && calculatorActions}
          </FormTitle>

          <TargetUsfContainer container>
            <Grid item xs={6}>
              <Headcount
                leftLabel={totalAvailableUsfTitle.replace(
                  'USF',
                  getUnit('area')
                )}
                centralLabel={TARGET_USF_LABEL.replace('USF', getUnit('area'))}
                centralInputClassName={!scenario?.targetUSF ? 'highlight' : ''}
                rightLabel="Work Seats"
                base={
                  convertToUnit(scenario?.totalAvailableUSF, {
                    unitType: 'area',
                  }) as number
                }
                setSecondary={(targetUsf: number) => {
                  let value = convertToOriginalUnit(targetUsf, {
                    unitType: 'area',
                  });
                  if (value > scenario?.totalAvailableUSF)
                    value = scenario?.totalAvailableUSF;

                  dispatch(
                    setScenarioValue({
                      factor: 'targetUsf',
                      value,
                    })
                  );
                }}
                inputStyles={{ width: 94 }}
                showRatio
                hideOp
                factorName="targetUsf"
                disabled={disableTargetUsfInput}
                factorValue={
                  convertToUnit(scenario?.targetUSF, {
                    unitType: 'area',
                    formatter: (v) =>
                      v?.toLocaleString('en', { maximumFractionDigits: 2 }),
                  }) as string
                }
                resultValue={scenario?.workSeats}
                operator="÷"
                showDecimals={showDecimals && !isEditingSavedScenario}
                testingIdPrefix="seatcount_analysis"
              />
            </Grid>
          </TargetUsfContainer>
          <FactorSection data-testid="headcount_analysis_title">
            CALCULATE WORK SEATS & HEADCOUNT
          </FactorSection>
          <FactorContainer container className="hidden-border mb-0 pb-0">
            <HeadcountContainer>
              <Headcount
                title="DESK SHARING RATIO"
                base={scenario?.workSeats}
                leftLabel="Work Seats"
                factorName="deskSharingRatio"
                tooltip={<DeskSharingGuide />}
                tooltipHeight={559}
                tooltipWidth={298}
                showRatio
                disabled={disableHeadcountInputs}
                factorValue={scenario?.deskSharingRatio?.toString()}
                resultValue={scenario?.headcountWithUtilization}
                operator="x"
                showDecimals={showDecimals && !isEditingSavedScenario}
                testingIdPrefix="headcount_analysis_left_box"
              />
            </HeadcountContainer>
            <Divider />
            <HeadcountContainer className="with-end-adornment">
              <Headcount
                title="HEADCOUNT UTILIZATION"
                base={scenario?.headcountWithUtilization}
                disabled={disableHeadcountInputs}
                factorName="headcountUtilization"
                tooltip={<HeadcountUtilizationGuide />}
                tooltipHeight={323}
                tooltipWidth={298}
                factorValue={scenario?.headcountUtilizationPercentage?.toString()}
                resultValue={scenario?.headcountWithVacancy}
                operator="÷"
                showDecimals={showDecimals && !isEditingSavedScenario}
                testingIdPrefix="headcount_analysis_middle_box"
              />
            </HeadcountContainer>
            <Divider />
            <HeadcountContainer className="with-end-adornment">
              <Headcount
                title="VACANCY FACTOR"
                factorName="vacancyFactor"
                tooltip={<VacancyFactorGuide />}
                tooltipHeight={255}
                tooltipWidth={298}
                base={scenario?.headcountWithVacancy}
                disabled={disableHeadcountInputs}
                factorValue={scenario?.vacancyFactorPercentage?.toString()}
                resultValue={scenario?.headcount}
                operator="x"
                showDecimals={showDecimals && !isEditingSavedScenario}
                testingIdPrefix="headcount_analysis_right_box"
              />
            </HeadcountContainer>
          </FactorContainer>
        </Container>
      </Paper>
    </Content>
  );
};

export default UsfScenario;
